'use client';

import { useEffect } from 'react';

import { getENV, usePageLayout } from '@libs/common-next';

import { useProduct } from '@/entities/product';

type CustomerInfo = {
    email: string;
    customerID: string;
    janrainID: string;
    registrationStatus: string;
};

type Attributes = {
    country: 'us' | 'ca';
    language: 'en' | 'en_CA';
    server: string;
    pageType: 'Product Details';
};

type Category = {
    primaryCategory: string;
    subCategory1: string;
    subCategory2: string;
    subCategory3: string;
};

type ProductCategory = Category & {
    categoryId: string;
    pageType: 'Product Detail';
};

type ProductInfo = {
    brand: string;
    productName: string;
    price: number;
    productID: string;
};

declare const window: {
    digitalData: {
        customer?: { customerInfo: CustomerInfo };
        pageData?: { attributes: Attributes; category: Category };
        product?: { category: ProductCategory; productInfo: ProductInfo };
    };
} & Window;

export const useDigitalData = () => {
    const { user, userData } = usePageLayout();

    const { product, base } = useProduct();

    const buildDigitalData = () => {
        // Customer Information
        const customerInfo = {
            email: userData?.profile?.emailAddress ?? '',
            customerID: user?.customerprofileid ? String(user.customerprofileid) : '',
            janrainID: '',
            registrationStatus: Boolean(user) && Boolean(userData) ? 'Existing Registration' : '',
        } satisfies CustomerInfo;

        // Page Data
        const country = getENV('NEXT_PUBLIC_LOCALE');
        const isCA = country.endsWith('CA');
        const attributes = {
            country: isCA ? 'ca' : 'us',
            language: isCA ? 'en_CA' : 'en',
            server: window.location.hostname,
            pageType: 'Product Details',
        } satisfies Attributes;

        const primaryCategoryName = base?.primaryCategory.name ?? '';
        const categoriesList = base?.categories.filter(category => category !== primaryCategoryName) ?? [];
        const category = {
            primaryCategory: primaryCategoryName,
            subCategory1: categoriesList[0] ?? '',
            subCategory2: categoriesList[1] ?? '',
            subCategory3: categoriesList[2] ?? '',
        } satisfies Category;

        // Product Information
        const productCategory = {
            categoryId: base?.primaryCategory.id ?? '',
            pageType: 'Product Detail',
            ...category,
        } satisfies ProductCategory;
        const productInfo = {
            productID: product?.sku ?? '',
            productName: product?.name ?? '',
            brand: product?.brand?.name ?? '',
            price: product?.price?.sale ?? product?.price?.original ?? 0.0,
        } satisfies ProductInfo;

        return {
            customer: { customerInfo },
            pageData: { attributes, category },
            product: { category: productCategory, productInfo },
        };
    };

    useEffect(() => {
        const digitalData = buildDigitalData();

        window.digitalData = { ...window.digitalData, ...digitalData };
    }, [user?.customerprofileid, product]);
};
